// Sync de eventos (F4) — empurra o log de eventos em lotes para a tabela
// public.events (append-only, RLS dono-somente). Diferente do sync de estado
// (LWW do blob inteiro), aqui nada se perde: é o dado bruto do MOAT,
// consultável por SQL para score/predição/benchmark server-side.
//
// Cursor local guarda o id do último evento enviado; upsert idempotente
// (ignoreDuplicates) tolera cursor perdido ou repetição de lote.

import type { TrackedEvent } from '@app-types/domain';
import { getState, subscribe } from '@core/store';
import { supabase } from './supabase';

const CURSOR_KEY = 'robox_events_cursor_v1';
const BATCH_MAX = 500;
const DEBOUNCE_MS = 5000;

let userId: string | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
let unsubStore: (() => void) | null = null;
let pushing = false;

function loadCursor(): string | null {
  try {
    return localStorage.getItem(CURSOR_KEY);
  } catch {
    return null;
  }
}

function saveCursor(id: string): void {
  try {
    localStorage.setItem(CURSOR_KEY, id);
  } catch {
    /* quota — próximo push repete o lote, upsert ignora duplicatas */
  }
}

/** eventos ainda não enviados (tudo após o cursor; cursor sumiu → tudo) */
function pendingEvents(events: TrackedEvent[]): TrackedEvent[] {
  const cursor = loadCursor();
  if (!cursor) return events;
  const idx = events.findIndex((e) => e.id === cursor);
  return idx === -1 ? events : events.slice(idx + 1);
}

async function pushBatch(): Promise<void> {
  if (!userId || pushing) return;
  const todo = pendingEvents(getState().events).slice(0, BATCH_MAX);
  if (todo.length === 0) return;
  pushing = true;
  try {
    const rows = todo.map((e) => ({
      id: e.id,
      user_id: userId,
      ts: e.ts,
      type: e.type,
      payload: e.payload,
    }));
    const { error } = await supabase
      .from('events')
      .upsert(rows, { onConflict: 'id', ignoreDuplicates: true });
    if (error) throw new Error(error.message);
    saveCursor(todo[todo.length - 1].id);
    // backlog maior que um lote → agenda o próximo
    if (pendingEvents(getState().events).length > 0) schedulePush();
  } catch (e) {
    console.error('[sync-events] push falhou:', e);
  } finally {
    pushing = false;
  }
}

function schedulePush(): void {
  if (!userId) return;
  if (timer) clearTimeout(timer);
  timer = setTimeout(pushBatch, DEBOUNCE_MS);
}

export function startEventSync(uid: string): void {
  if (userId === uid && unsubStore) return;
  stopEventSync();
  userId = uid;
  schedulePush(); // sobe o backlog acumulado offline/pré-login
  unsubStore = subscribe(schedulePush);
}

export function stopEventSync(): void {
  if (unsubStore) {
    unsubStore();
    unsubStore = null;
  }
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  userId = null;
}
